<template>
  <ts-card style="height: inherit;" class="ts-flex ts-flex-col">
    <div class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative">
      <h4 class="ts-text-sm">{{ $t('role.pageTitle') }}</h4>
      <ts-modal-close-button />
    </div>
    <ts-card-wrapper class="ts-flex-grow ts-overflow-hidden">
      <form class="ts-overflow-hidden ts-h-full ts-flex ts-flex-col">
        <div class="row">
          <div class="col-sm-12">
            <div class="form-group m-b-10 ts-flex">
              <label class="col-form-label control-label required">{{ $t('role.name') }}</label>
              <div class="ts-ml-6">
                <ts-input v-model="model.name" :class="{ 'is-invalid': errors.has('name') }" autofocus />
                <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="ts-flex-grow ts-overflow-auto ts-mt-6">
          <ts-table>
            <thead slot="thead">
              <tr>
                <th class="ts-sticky ts-top-0 ts-z-10">
                  <span class="ts-inline-block ts-w-full ts-text-center" v-if="loadingPermission">
                    {{ $t('role.loadingPermissions') }}
                  </span>
                  <ts-input
                    v-model="searchPermissionText"
                    :placeholder="$t('role.searchPermission')"
                    style="width: 170px;"
                    v-else
                  />
                </th>
                <th
                  v-for="column in permissionColumns"
                  :key="column"
                  class="ts-sticky ts-top-0 ts-z-10 ts-w-20 ts-text-center"
                >
                  <div class="ts-flex ts-flex-col ts-justify-center ts-items-center ts-w-full">
                    <p>{{ column }}</p>
                    <ts-checkbox
                      :checkedValue="allCheckedBy(column)"
                      @change="checked => selectAllBy(column, checked)"
                    />
                  </div>
                </th>
              </tr>
              <TableProgressBar v-show="loadingPermission" :columnLength="permissionColumns.length + 1" />
            </thead>
            <tbody slot="tbody">
              <template v-for="(group, groupName) in permissionGroups">
                <tr>
                  <td>
                    <span class="ts-text-sm ts-font-semibold ts-pt-6 ts-inline-block">{{ groupName }}</span>
                  </td>
                  <td v-for="n in permissionColumns.length" :key="n">
                    <div class="ts-flex ts-justify-center ts-pt-6">
                      <ts-checkbox
                        :checkedValue="allCheckedByGroup(groupName, permissionColumns[n - 1])"
                        @change="checked => selectByGroup(groupName, permissionColumns[n - 1], checked)"
                      />
                    </div>
                  </td>
                </tr>
                <tr v-for="(permissions, resourceName) in group" :key="resourceName">
                  <td>
                    <span class="ts-ml-4 ts-mr-6">{{ resourceName }}</span>
                  </td>
                  <td v-for="n in permissionColumns.length" :key="n">
                    <div class="ts-flex ts-justify-center">
                      <ts-checkbox
                        v-model="model.permissions"
                        :value="permissions[n - 1].id"
                        v-if="permissions[n - 1] !== undefined"
                      />
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </ts-table>
        </div>
        <div class="row ts-mt-6">
          <div class="col-sm-12 col-lg-12 col-md-12 text-right">
            <ts-button @click.prevent="$emit('close')" :disabled="saving">{{ $t('button.cancel') }}</ts-button>
            <ts-button
              outline
              color="primary"
              class="ml-2"
              :waiting="savingAndNew"
              @click.prevent="saveAndNew"
              v-if="editData === undefined"
              >{{ $t('button.createAndNew') }}</ts-button
            >
            <ts-button color="primary" class="ml-2" :waiting="saving" @click.prevent="save">{{
              editData ? $t('button.update') : $t('button.create')
            }}</ts-button>
          </div>
        </div>
      </form>
    </ts-card-wrapper>
  </ts-card>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { cloneDeep, union, uniq, difference, intersection, flatMap, map, filter } from 'lodash'
import { mapState, mapGetters } from 'vuex'
import TableProgressBar from '@/components/common/TsTable/TableProgressBar'

export default {
  name: 'role-form',
  components: { TableProgressBar },
  props: ['editData'],
  data() {
    const model = {
      name: this.editData ? this.editData.name : '',
      permissions: this.editData ? this.editData.permissions.map(p => p.id) : []
    }

    return {
      newModel: model,
      model: cloneDeep(model),
      errors: new Errors(),
      saving: false,
      savingAndNew: false,
      loadingPermission: false
    }
  },
  computed: {
    ...mapState('security/role', ['permissions']),
    ...mapGetters('security/role', ['permissionGroups', 'permissionColumns']),
    searchPermissionText: {
      get() {
        return this.$store.state.security.role.searchPermissionText
      },
      set(newValue) {
        this.$store.commit('security/role/SET_SEARCH_PERMISSION', newValue)
      }
    },
    idPermissionsBy: vm => column => {
      return uniq(
        map(
          filter(vm.permissions, p => {
            return p.name.toLowerCase().startsWith(column.toLowerCase())
          }),
          'id'
        )
      )
    },
    allCheckedBy: vm => column => {
      const idPermissions = vm.idPermissionsBy(column)

      return intersection(vm.model.permissions, idPermissions).length === idPermissions.length
    },
    idPermissionsGroup: vm => (groupName, column) => {
      return uniq(
        map(
          filter(flatMap(vm.permissionGroups[groupName]), p => {
            return p.name.toLowerCase().startsWith(column.toLowerCase())
          }),
          'id'
        )
      )
    },
    allCheckedByGroup: vm => (groupName, column) => {
      const idPermissions = vm.idPermissionsGroup(groupName, column)

      return intersection(vm.model.permissions, idPermissions).length === idPermissions.length
    }
  },
  created() {
    this.fetchPermissions()
    this.searchPermissionText = ''
  },
  methods: {
    selectAllBy(column, checked) {
      if (checked) {
        this.model.permissions = union(this.model.permissions, this.idPermissionsBy(column))
      } else {
        this.model.permissions = difference(this.model.permissions, this.idPermissionsBy(column))
      }
    },
    selectByGroup(groupName, column, checked) {
      if (checked) {
        this.model.permissions = union(this.model.permissions, this.idPermissionsGroup(groupName, column))
      } else {
        this.model.permissions = difference(this.model.permissions, this.idPermissionsGroup(groupName, column))
      }
    },
    fetchPermissions() {
      this.loadingPermission = true

      this.$store.dispatch('security/role/fetchPermissions').finally(() => (this.loadingPermission = false))
    },
    submit() {
      let responseResult

      this.errors = new Errors()

      if (this.editData) {
        responseResult = this.$store.dispatch('security/role/update', {
          id: this.editData.id,
          ...this.model
        })
      } else {
        responseResult = this.$store.dispatch('security/role/create', this.model)
      }

      return responseResult
        .then(response => {
          return this.$store.dispatch('security/role/fetch').then(res => {
            this.saving = false
            this.$toasted.success(response.message)
            return Promise.resolve(res)
          })
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)

          return Promise.reject(error)
        })
    },
    save() {
      this.saving = true

      this.submit()
        .then(() => {
          this.$emit('close')
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveAndNew() {
      this.savingAndNew = true

      this.submit()
        .then(() => {
          this.model = cloneDeep(this.newModel)
        })
        .finally(() => {
          this.savingAndNew = false
        })
    }
  }
}
</script>
